import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import type { PaginatedResponse, TenantUser, TenantUserDevice } from "./types";

export function useTenantUsers(tenantId: string, params?: Record<string, string>) {
  return useQuery<PaginatedResponse<TenantUser>>({
    queryKey: ["tenant-users", tenantId, params],
    queryFn: async () => {
      const { data } = await client.get(`/tenants/${tenantId}/users/`, { params });
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useCreateTenantUser(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      email: string;
      first_name: string;
      last_name: string;
      role: string;
      password?: string;
    }) => {
      const { data } = await client.post(`/tenants/${tenantId}/users/`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] }),
  });
}

export function useRemoveTenantUser(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await client.post(`/tenants/${tenantId}/users/${userId}/remove/`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] }),
  });
}

export function useTenantUserDevices(tenantId: string, userId: string) {
  return useQuery<{ data: TenantUserDevice[] }>({
    queryKey: ["tenant-users", tenantId, userId, "devices"],
    queryFn: async () => {
      const { data } = await client.get(`/tenants/${tenantId}/users/${userId}/devices/`);
      return data;
    },
    enabled: !!tenantId && !!userId,
  });
}

export function useAssignDevice(tenantId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { device_id: string; is_primary?: boolean }) => {
      const { data } = await client.post(`/tenants/${tenantId}/users/${userId}/assign_device/`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] });
    },
  });
}

export interface ImportUsersResult {
  created: number;
  skipped: number;
  devices_mapped: number;
  errors: string[];
}

export function useImportTenantUsers(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<ImportUsersResult, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await client.post(`/tenants/${tenantId}/users/import/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] }),
  });
}

export function useUnassignDevice(tenantId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (deviceId: string) => {
      const { data } = await client.post(`/tenants/${tenantId}/users/${userId}/unassign_device/`, { device_id: deviceId });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] });
    },
  });
}
